import { useState } from 'react'
import InsightCard from './InsightCard'

// Renders the named threat actors most relevant to this client based on
// vertical match + Client Intelligence signals. Each actor expands to show
// recent campaigns, primary access vectors, and the specific reasons our
// engine flagged them for this client.

// Actor type — dot + label, no pill. Matches Linear/Stripe/Vercel discreet
// status patterns rather than chunky badges.
const TYPE_TONES = {
  ransomware:        { label: 'Ransomware',     dot: 'bg-red-500',    text: 'text-red-700 dark:text-red-400' },
  extortion:         { label: 'Extortion',      dot: 'bg-red-500',    text: 'text-red-700 dark:text-red-400' },
  apt:               { label: 'Nation-State',   dot: 'bg-purple-500', text: 'text-purple-700 dark:text-purple-400' },
  bec:               { label: 'BEC',            dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-400' },
  'financial-fraud': { label: 'Financial Fraud',dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-400' },
  iab:               { label: 'Initial Access', dot: 'bg-blue-500',   text: 'text-blue-700 dark:text-blue-400' }
}

function TypeBadge({ type }) {
  const t = TYPE_TONES[type] || { label: type || 'Group', dot: 'bg-gray-400', text: 'text-gray-600 dark:text-gray-400' }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium">
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} aria-hidden />
      <span className={t.text}>{t.label}</span>
    </span>
  )
}

// Confidence — text-only with subtle parenthetical, no background
function ConfidencePill({ value }) {
  if (!value) return null
  return (
    <span className="text-[11px] text-gray-500 dark:text-gray-400 capitalize" title="Confidence in current targeting attribution">
      ({value} confidence)
    </span>
  )
}

function ActorRow({ actor, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div className={`rounded-lg border ${open ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40'} transition-colors`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start justify-between gap-3 p-3 text-left"
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm font-bold text-gray-900 dark:text-white truncate">{actor.name}</span>
          <TypeBadge type={actor.type} />
          <ConfidencePill value={actor.confidence} />
        </div>
        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Always-visible relevance reason — the most important context */}
      {actor.relevanceReasons?.length > 0 && (
        <div className="px-3 pb-2 text-xs text-gray-600 dark:text-gray-400">
          {actor.relevanceReasons[0]}
        </div>
      )}

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-3 text-xs space-y-3">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <dt className="font-semibold text-gray-700 dark:text-gray-300">Attribution</dt>
              <dd className="text-gray-600 dark:text-gray-400 mt-0.5">{actor.attribution || 'Unknown'}</dd>
            </div>
            <div>
              <dt className="font-semibold text-gray-700 dark:text-gray-300">Motivation</dt>
              <dd className="text-gray-600 dark:text-gray-400 mt-0.5">{(actor.motivations || []).join(', ') || '—'}</dd>
            </div>
          </dl>

          {actor.primaryAccessVectors?.length > 0 && (
            <div>
              <dt className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Primary Access Vectors</dt>
              <ul className="space-y-0.5">
                {actor.primaryAccessVectors.map((v, i) => (
                  <li key={i} className="text-gray-600 dark:text-gray-400 flex items-start">
                    <span className="text-primary-500 mr-1.5 mt-0.5">•</span>{v}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {actor.recentNotableIncidents?.length > 0 && (
            <div>
              <dt className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Recent Notable Incidents</dt>
              <ul className="space-y-1">
                {actor.recentNotableIncidents.map((inc, i) => (
                  <li key={i} className="text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{inc.year} — {inc.target}:</span> {inc.context}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {actor.relevanceReasons?.length > 1 && (
            <div>
              <dt className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Why this actor is flagged for this client</dt>
              <ul className="space-y-0.5">
                {actor.relevanceReasons.map((r, i) => (
                  <li key={i} className="text-gray-600 dark:text-gray-400 flex items-start">
                    <span className="text-primary-500 mr-1.5 mt-0.5">•</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function RelevantThreatActorsCard({ tuned }) {
  if (!tuned || !Array.isArray(tuned.namedActors) || tuned.namedActors.length === 0) {
    return (
      <InsightCard
        title="Relevant Threat Actors"
        subtitle="Named groups whose targeting matches this client's vertical and observed signals"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          No actor matches available. Run Client Intelligence enrichment to surface specific groups whose recent campaigns align with this client's surface area.
        </p>
      </InsightCard>
    )
  }

  return (
    <InsightCard
      title="Relevant Threat Actors"
      subtitle={
        tuned.enrichmentAvailable
          ? `${tuned.namedActors.length} groups ranked by vertical match and Client Intelligence signals`
          : `${tuned.namedActors.length} groups ranked by vertical match (run Client Intelligence to refine)`
      }
    >
      <div className="space-y-2">
        {tuned.namedActors.map((actor, idx) => (
          <ActorRow key={actor.id} actor={actor} defaultOpen={idx === 0} />
        ))}
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 italic mt-3">
        Sourced from public CISA #StopRansomware advisories, FBI IC3 reports, and major threat-intel publications. Last reviewed 2026-04-25. Targeting and campaign data shifts quarterly.
      </p>
    </InsightCard>
  )
}

export default RelevantThreatActorsCard
